import { Component, ElementRef, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { getButtonTitle, getColumnsMailsLinked, getColumnsScan, getTabsScan } from './home-iscan-config';
import { IScanAPIService } from 'src/app/shared/services/iscan.service';
import { ICourrierScanService } from 'src/app/shared/services/iCourrier-scan.service';
import {
  ScanFromCourrier,
  ScanFromCourrierPaginate,
  ScanFromCourrierTableInfos,
  splitScan,
} from 'src/app/shared/models/iCourrier/scans/scan-from-courrier';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { addDays } from 'date-fns';
import saveAs from 'file-saver';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { NgxSpinnerService } from 'ngx-spinner';
import { UsersService } from 'src/app/shared/services/users.service';
import { UserDto } from 'src/app/shared/interfaces/users';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { NotifyUserSignalrPayload, SignalrPayloadEventId } from 'src/app/shared/models/signalr_payloads';
import { environment } from 'src/environments/environment';
import { MeService } from 'src/app/shared/services/me.service';
import { UploadFileParams, UploadScan } from 'src/app/shared/models/iScanAPI/upload-scan';
import { AreaDropEvent, DroppableArea, IModalHeader, IModalParams } from '@devinforius/i-essentials/lib/interfaces';
import { IButton, TranslateService } from '@devinforius/i-essentials';
import { ITab } from '@devinforius/i-navigation/lib/models/i-tabsXl';
import { ITableColumn } from '@devinforius/i-table';

type IScanMode = 'none' | 'barcode' | 'barcodewithout' | 'qrcode' | 'qrcodewithout';

@Component({
  selector: 'app-home-iscan',
  templateUrl: './home-iscan.component.html',
  styleUrls: ['./home-iscan.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HomeIscanComponent implements OnInit, OnDestroy {
  @ViewChild('overlayButtonNewScan') overlayButtonNewScan: OverlayPanel;
  @ViewChild('fileInput') fileInput: ElementRef;

  public notifyUserSubscription: Subscription;

  public buttons: IButton[];
  public tabs: ITab[];
  public tabsArrayXLActiveID: 'toProcess' | 'processed' | 'history' = 'toProcess';

  public modalParams: IModalParams;
  public modalHeader: IModalHeader;
  public modalHeaderSeparatorPage: IModalHeader;
  public columns: ITableColumn[]; // columns for list of scans
  public columnsMailsLink: ITableColumn[]; // columns for list of mails
  public items: ScanFromCourrier[] = [];
  public itemsMailsLinked: splitScan[] = [];
  public menu: MenuItem[] = [];
  public totalRecords: number;
  public rows = 10;
  public first = 0;
  public pageNumber = 1;

  public openDialogMailsLinked = false;
  public openDialogCreateSeparatorPage = false;

  public uniqueUserIds: number[] = [];
  public isGestionnaire = false;

  // Each mode is linked : none = Manuel  /  qrcode = QrCode   /   qrcodewithout = SeparateurWithout
  private modeiScan = signal<IScanMode>('none');
  public modeiCourrier: 'Manuel' | 'QrCode' | 'SeparateurWithout'; // barcode and barcodewithout don't exist in iCourrier

  public droppableAreas = signal<DroppableArea[]>([
    {
      identifier: 1,
      text: 'iScan.globalInfos.manualSeparation',
    },
    {
      identifier: 2,
      text: 'iScan.globalInfos.QRCodeKeepingSeparators',
    },
    {
      identifier: 3,
      text: 'iScan.globalInfos.QRCodeNotKeepingSeparators',
    },
  ]);

  private maxSizeFile: number = 270_000_000;
  private iCourrierURLByEnvironment = environment.production ? '/iCourriers' : '/iCourriers2nd';
  private datasForFileUpload: UploadFileParams = {
    file: [],
    splitMode: null,
    callback: environment.apiGatewayBaseUrl + this.iCourrierURLByEnvironment + '/api/v2/Connecteurs/IScan/Status',
  };

  public tableLoaded = false;

  constructor(
    public translater: TranslateService,
    public iScanAPIService: IScanAPIService,
    public iCourrierScanService: ICourrierScanService,
    public toastr: ToastrService,
    public router: Router,
    private confirmationService: ConfirmationService,
    private spinner: NgxSpinnerService,
    private meService: MeService,
    private userService: UsersService,
    public signalRService: SignalrService
  ) {}

  ngOnInit(): void {
    this.notifyUserSubscription = this.signalRService.notifyUser.subscribe({
      next: (payload: NotifyUserSignalrPayload) => {
        if (payload.payloadEventId == SignalrPayloadEventId.IScanLotStatusChanged) {
          this.getItems('toProcess', 1);
        }
      },
    });
    this.droppableAreas.update((areas) => [...areas.map((area) => ({ ...area, text: this.translater.translate(area.text) }))]);
    this.buttons = getButtonTitle(this, this.translater);
    this.columnsMailsLink = getColumnsMailsLinked(this.translater);
    this.tabs = getTabsScan(this.translater);
    this.getItems('toProcess', 1);
    this.setMenuAction();
    this.setModalConfiguration();
    // All of the methods up there don't call any request so they're done following each other
    this.getRightManagements().then(() => {
      this.callChainedGetContact();
    });
  }

  ngOnDestroy(): void {
    if (localStorage.getItem('i-table-state-scanList')) {
      const dataTable = JSON.parse(localStorage.getItem('i-table-state-scanList'));
      dataTable.first = 0;
      dataTable.rows = 10;
      localStorage.setItem('i-table-state-scanList', JSON.stringify(dataTable));
    }
  }

  /*
    Setting the actions for the ... button for the iTable scans
  */
  setMenuAction(): void {
    switch (this.tabsArrayXLActiveID) {
      case 'toProcess':
        this.menu = [
          {
            label: this.translater.translate('iScan.action.modifyScan'),
            id: 'modifyScan',
            icon: 'bi bi-pencil-square',
            tooltip: this.translater.translate('iScan.action.modifyScan'),
            command: (event) => {
              const scanChosen = this.items.find((item) => item.guid === (event as ScanFromCourrierTableInfos).guid);
              this.modifyScan(scanChosen);
            },
            condition: true,
          },
          {
            label: this.translater.translate('iScan.action.downloadDocument'),
            id: 'downloadDocument',
            icon: 'bi bi-download',
            tooltip: this.translater.translate('iScan.action.downloadDocument'),
            command: (event): void => {
              const scanChosen = this.items.find((item) => item.guid === (event as ScanFromCourrierTableInfos).guid);
              this.downloadDocument(scanChosen);
            },
            condition: true,
          },
          {
            label: this.translater.translate('iScan.action.abortScan'),
            id: 'abortScan',
            icon: 'bi bi-trash3-fill',
            tooltip: this.translater.translate('iScan.action.abortScan'),
            command: (event): void => {
              const scanChosen = this.items.find((item) => item.guid === (event as ScanFromCourrierTableInfos).guid);
              this.verifyStatusDeleteScan(scanChosen);
            },
            condition: true,
          },
        ];
        break;
      case 'processed':
        this.menu = [
          {
            label: this.translater.translate('iScan.action.seeScan'),
            icon: 'bi bi-eye-fill',
            tooltip: this.translater.translate('iScan.action.seeScan'),
            command: (event): void => {
              this.openDialogMailsLinked = true;
              const scanChosen = this.items.find((item) => item.guid === (event as ScanFromCourrierTableInfos).guid);
              this.getItemsMailsLinked(scanChosen);
            },
            condition: true,
          },
        ];
        break;
      case 'history':
        this.menu = [{}];
        break;
    }
    this.tableLoaded = true;
  }

  /*
    Sets the config for the modal header and params
  */
  setModalConfiguration(): void {
    this.modalHeader = {
      title: this.translater.translate('iScan.information.mailsLinkedInfos'),
      iconTitle: 'bi bi-link-45deg',
      showNotification: false,
      showParameters: false,
      closable: true,
    };
    this.modalHeaderSeparatorPage = {
      title: this.translater.translate('iScan.action.generateSeparatorPage'),
      showNotification: false,
      showParameters: false,
      closable: true,
    };
    this.modalParams = {
      isDraggable: true,
      isResizable: true,
      isModal: true,
      style: { width: '70vw', minWidth: '70vw', minHeight: '70vh' },
    };
  }

  private getRightManagements(): Promise<boolean> {
    return new Promise((resolve) => {
      this.meService.rightManagement().subscribe({
        next: (res) => {
          this.isGestionnaire = res.menu.isAdminIscan;
          resolve(true);
        },
        error: () => {
          this.toastr.error(this.translater.translate('iScan.error.errorRetrievingRightManagements'));
        },
      });
    });
  }

  /*
  Gets all the scans from a specific tab
  */
  getItems(tabs: 'toProcess' | 'processed' | 'history', pageNumber: number): void {
    const data = {
      pageNumber: pageNumber,
      PageSize: this.rows,
      StIds: tabs === 'toProcess' ? [1, 2, 3, 4] : tabs === 'processed' ? [5] : [1, 2, 3, 4, 5],
    };
    this.iCourrierScanService.getScanLots(data).subscribe({
      next: (res: ScanFromCourrierPaginate) => {
        this.totalRecords = res.totalCount;
        this.rows = res.pageSize;
        this.first = res.pageNumber - 1;
        this.items = res.items;
        this.pageNumber = res.pageNumber;
        this.uniqueUserIds = Array.from(new Set(this.items.map((item) => item.userId)));
      },
      error: () => {
        this.toastr.error(this.translater.translate('iScan.error.errorRetrievingScan'));
      },
    });
  }

  /*
    Getting the rights to display, or not, the column 'Contact' which displays the contact who created the scan
    then displays if isGestionnaire === true
  */
  callChainedGetContact(): void {
    if (this.isGestionnaire) {
      this.getUsersCreatedScans().subscribe({
        next: (userList: UserDto[]) => {
          const initColumn = getColumnsScan(this.tabsArrayXLActiveID, this.translater);
          initColumn.splice(initColumn.length - 2, 0, {
            field: 'creatorOfScanLabel',
            header: this.translater.translate('creator'),
            width: '15%',
            format: (_value, item) => {
              if (item?.userId) {
                const user = userList.find((user) => user.userId === item.userId);
                return user ? `${user.contactForUser?.nom} ${user.contactForUser?.prenom}` : '/';
              } else {
                return '/';
              }
            },
          });
          this.columns = initColumn;
        },
        error: () => {
          return false;
        },
      });
    } else {
      this.columns = getColumnsScan(this.tabsArrayXLActiveID, this.translater);
    }
    this.tableLoaded = true;
  }
  /*
    Getting all the contacts that created a scan
  */
  getUsersCreatedScans(): Observable<UserDto[]> {
    const data = {
      UserIds: this.uniqueUserIds.toString(),
    };
    return this.userService.fetchUsersFromiAdmin(data);
  }

  /*
  Triggers when the user drops a scan
  */
  onScanDrop(event: AreaDropEvent): void {
    switch (event.areaIdentifier) {
      case 1:
        this.setMode('Manual');
        break;
      case 2:
        this.setMode('QRCode');
        break;
      case 3:
        this.setMode('QRCodeNotKept');
        break;
    }
    this.setInfosForUploadScanFromDragNDrop(event.files);
  }

  /*
  Triggers when the user changes the active tab
  */
  changeTab(event: ITab): void {
    this.tableLoaded = false;
    this.items = [];
    this.getItems(event.id as 'toProcess' | 'processed' | 'history', 1);
  }

  public changeCurrentTabId(idOfCurrentTab: 'toProcess' | 'processed' | 'history'): void {
    this.tabsArrayXLActiveID = idOfCurrentTab;
    this.setMenuAction();
  }

  /*
    Triggered when the lazy load event is triggered 
  */
  onLazyLoad(event: { first: number; rows: number }): void {
    this.rows = event.rows;
    this.pageNumber = event.first / event.rows + 1;
    this.getItems(this.tabsArrayXLActiveID, this.pageNumber);
  }

  /*
  Triggers when user clicks on a row in the table or when user clicks on modify action on the ... on the iTable
  */
  modifyScan(event: ScanFromCourrier): void {
    if (this.tabsArrayXLActiveID === 'toProcess') {
      this.verifyStatus(event).then((status) => {
        if (status === 2) {
          this.router.navigate(['/private/ic/iscan/detail/' + event.guid]);
        }
      });
    } else if (this.tabsArrayXLActiveID === 'processed') {
      this.openDialogMailsLinked = true;
      this.getItemsMailsLinked(event);
      return;
    }
  }

  verifyStatus(event: ScanFromCourrier): Promise<number> {
    return new Promise((resolve) => {
      if (event.lastTransition.statutId === 1) {
        this.iScanAPIService.getStatus(event.guid).subscribe({
          next: (resp) => {
            if (resp.status === 'completed') {
              this.updateStatutICourrierScan(event).then((updatedStatus) => {
                resolve(updatedStatus);
              });
            } else {
              const dateExpired = addDays(new Date(event.lastTransition.date), 1);
              if (dateExpired < new Date()) {
                this.toastr
                  .warning(
                    this.translater.translate('iScan.error.pleaseContactSupport'),
                    this.translater.translate('iScan.error.treatmentDelayIsTooLong'),
                    { closeButton: true, progressBar: true }
                  )
                  .onTap.pipe(take(1))
                  .subscribe({
                    next: () => {
                      this.router.navigate(['/private/assistance']);
                    },
                  });
              } else {
                this.toastr.warning(
                  this.translater.translate('iScan.information.scanBeingProcessed'),
                  this.translater.translate('iScan.warning.warning')
                );
              }
              resolve(-1);
            }
          },
        });
      } else {
        resolve(event.lastTransition.statutId);
      }
    });
  }

  /*
  Will update the status of the scan sent 
  */
  updateStatutICourrierScan(event: ScanFromCourrier): Promise<number> {
    return new Promise((resolve) => {
      const data = {
        lotGuids: [event.guid],
        status: 'Processed',
      };
      this.iCourrierScanService.updateStatusScan(data).subscribe({
        next: () => {
          this.toastr.success(this.translater.translate('iScan.information.statusUpdated'), this.translater.translate('iScan.success.success'));
          this.getItems('toProcess', this.pageNumber);
          resolve(2);
        },
        error: (error) => {
          this.toastr.error(error.message, this.translater.translate('iScan.error.errorRetrievingStatus'));
          resolve(-1);
        },
      });
    });
  }

  /*
    Code executed when user wants to download the file
  */
  downloadDocument(event: ScanFromCourrier): void {
    this.iScanAPIService.downloadFile(event.guid).subscribe({
      next: (resp) => {
        const downloadURL = URL.createObjectURL(resp);
        saveAs(downloadURL, event.filename);
        this.toastr.success(this.translater.translate('iScan.success.download'));
      },
      error: () => {
        this.toastr.error(this.translater.translate('iScan.error.download'));
      },
    });
  }

  /*
    Will verify if the scan can be aborted and then calls the confirm method
  */
  verifyStatusDeleteScan(event: ScanFromCourrier): void {
    this.verifyStatus(event).then((status) => {
      if (status === 2) {
        this.confirmDeleteScan(event);
      }
    });
  }

  /*
    Confirmation asked to user to delete the scan
  */
  confirmDeleteScan(event: ScanFromCourrier): void {
    // Message de base : Suppression: pas de récupération. En effet si 0 courrier lié, le scan va être supprimé simplement
    // Par contre s'il existe au moins un courrier lié, alors le scan va etre "archivé" et passer directement dans "Traités"
    let messageDisplayedInConfirmModal =
      this.translater.translate('iScan.information.wantDeleteScan') + ' ' + this.translater.translate('iScan.information.youCantRecover');
    if (event.splits.some((split) => split.courrierId)) {
      messageDisplayedInConfirmModal =
        this.translater.translate('iScan.information.scanWillBeArchived') +
        ' ' +
        this.translater.translate('iScan.information.canFindScanInProcessedTab');
    }
    this.confirmationService.confirm({
      message: messageDisplayedInConfirmModal,
      key: 'iScanDeletionConfirmationKey',
      accept: () => this.deleteScan(event),
    });
  }

  /*
    Deletion of the scan
  */
  deleteScan(event: ScanFromCourrier): void {
    this.spinner.show();
    this.iCourrierScanService.deleteScan([event.guid]).subscribe({
      next: () => {
        this.toastr.success(
          this.translater.translate('iScan.success.scanWasSuccessfullyDeleted', { scanLabel: event.filename }),
          this.translater.translate('iScan.success.success')
        );
        this.getItems('toProcess', this.pageNumber);
        this.spinner.hide();
      },
      error: () => {
        this.toastr.error(this.translater.translate('iScan.error.errorSuppression'));
        this.spinner.hide();
      },
    });
  }

  getItemsMailsLinked(event: ScanFromCourrier): void {
    this.itemsMailsLinked = event.splits;
  }

  showDetailsSelectedMail(scan: splitScan): void {
    const config = environment;
    const baseUrl = config.baseUrl;
    const url = '/private/ic/tabs/';
    window.open(baseUrl + url + scan.courrierId, '_blank');
  }

  public setMode(mode: 'Manual' | 'QRCode' | 'QRCodeNotKept') {
    switch (mode) {
      case 'Manual':
        this.modeiScan.set('none');
        this.modeiCourrier = 'Manuel';
        break;
      case 'QRCode':
        this.modeiScan.set('qrcode');
        this.modeiCourrier = 'QrCode';
        break;
      case 'QRCodeNotKept':
        this.modeiScan.set('qrcodewithout');
        this.modeiCourrier = 'SeparateurWithout';
        break;
    }
  }

  public openFileSelectionModalAndHideOverlay(): void {
    this.fileInput.nativeElement.click();
    this.overlayButtonNewScan.hide();
  }

  public setInfosForUploadScanFromButton(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      this.toastr.error(this.translater.translate('iScan.error.noFileSelected'), this.translater.translate('iScan.error.error'));
      return;
    }

    if (this.doesOneFileExceedSizeLimit(input.files)) {
      this.toastr.error(this.translater.translate('iScan.error.maxSizeReached'), this.translater.translate('iScan.error.fileTooLarge'));
      return;
    }

    const file: FileList = input.files;

    this.datasForFileUpload = {
      ...this.datasForFileUpload,
      file: [...Array.from(file)],
      splitMode: this.modeiScan(),
    };
    this.uploadFile(input);
  }

  private setInfosForUploadScanFromDragNDrop(listOfFiles: FileList): void {
    if (this.doesOneFileExceedSizeLimit(listOfFiles)) {
      this.toastr.error(this.translater.translate('iScan.error.maxSizeReached'), this.translater.translate('iScan.error.oneFileIsTooLarge'));
      return;
    }

    if (!Array.from(listOfFiles)?.length) {
      this.toastr.error(this.translater.translate('iScan.error.noFileSelected'), this.translater.translate('iScan.error.error'));
      return;
    }

    this.datasForFileUpload = {
      ...this.datasForFileUpload,
      file: [...Array.from(listOfFiles)],
      splitMode: this.modeiScan(),
    };
    this.uploadFile();
  }

  uploadFile(input?: HTMLInputElement): void {
    const toastRef = this.toastr.info('En cours de chargement', 'Chargement', {
      timeOut: 0,
      extendedTimeOut: 0,
      tapToDismiss: false,
    });
    this.iScanAPIService.upload(this.datasForFileUpload).subscribe({
      next: (uploadedFiles: UploadScan[]) => {
        const scanCreations = uploadedFiles.map((uploadedFile) =>
          this.iCourrierScanService.createScan({
            lotGuid: uploadedFile.guid,
            filename: uploadedFile.name,
            uploadDate: new Date(),
            scanType: this.modeiCourrier,
            pagesCount: uploadedFile.numPages,
          })
        );

        forkJoin(scanCreations).subscribe({
          next: () => {
            this.toastr.clear(toastRef.toastId);
            this.toastr.success(this.translater.translate('iScan.success.scanCreate'), this.translater.translate('iScan.success.success'));
            input ? (input.value = '') : null;
            this.getItems('toProcess', 1);
          },
          error: (error) => {
            input ? (input.value = '') : null;
            this.toastr.clear(toastRef.toastId);
            this.toastr.error(error.message, this.translater.translate('iScan.error.error'));
          },
        });
      },
      error: (error) => {
        input ? (input.value = '') : null;
        this.toastr.clear(toastRef.toastId);
        this.toastr.error(error.message, this.translater.translate('iScan.error.error'));
      },
    });
  }

  doesOneFileExceedSizeLimit(files: FileList): boolean {
    return Array.from(files).some((f) => f.size > this.maxSizeFile);
  }
  onNativeDrop(event: DragEvent): void {
  event.preventDefault();

  let target = event.target as HTMLElement | null;
  let areaIdentifier: number | null = null;

  while (target) {
    const id = target.id ?? '';
    if (id.startsWith('area__')) {
      areaIdentifier = +id.split('__')[1];
      break;
    }
    target = target.parentElement;
  }

  if (!areaIdentifier) return;

  const files = event.dataTransfer?.files;
  if (!files?.length) return;

  this.onScanDrop({ areaIdentifier, files });
}
}
